import styled from 'styled-components'
import { t } from 'ttag'
import { useCallback, useEffect, useContext } from 'react'

import { features } from '@h2/components/media'
import { PG, NAVIANCEBLUE } from '@h2/components/colors'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Field } from '@h2/components/inputs/field'
import { TextInput } from '@h2/components/inputs/text'
import { useForm } from '@h2/common/components/hooks/useForm'
import { Link } from '@h2/common/components/link'
import { Card } from '@h2/common/components/card'
import { Globals } from '@h2/globals'
import { Button } from '@h2/components/buttons/button'

import { props } from './props'

import { Create } from '../highlights/create'
import { Controls } from '../controls'
import { Submit } from '../submit'
import { Back } from '../back'

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;

  > div:last-child {
    margin-top: 35px;
    flex-direction: column-reverse;
    gap: 35px;

    > div {
      margin-left: 0;
      width: 100%;
    }
  }

  @media (${features.minWidth.laptop}) {
    > div:last-child {
      margin-top: auto;
      flex-direction: row;
      gap: normal;

      > div {
        margin-left: auto;
        width: 250px;
      }
    }
  }
`
const ButtonLink = styled(Button)`
  text-decoration: underline;
  margin-top: 15px;

  @media (${features.minWidth.laptop}) {
    margin-top: 35px;
  }
`
const Username = ({
  inDialog,
  properties,
  validate,
  isValidating,
  formErrors,
  renderPanel
}) => {
  const { register, error, handleOnSubmit, isInvalid, setValidation, setValue } = useForm(
    {
      name(form) {
        if (!form.name?.trim()) {
          return t`Username is required`
        }

        if (form.name?.trim().length < 4) {
          return t`Must be at least 4 characters`
        }
      }
    }
  )
  const { isNavianceUser } = useContext(Globals)
  const { isBelow13 } = properties
  const onSubmit = useCallback(form => validate({ ...form, noEmail: true }), [validate])

  useEffect(() => {
    if (formErrors) {
      setValidation(formErrors)
    }
  }, [formErrors, setValidation])

  useEffect(() => {
    if (properties.name) {
      setValue({ name: properties.name })
    }
  }, [properties.name, isNavianceUser, setValue])

  return (
    <Card useDetour inDialog={inDialog}>
      <Card.Highlight color={isNavianceUser ? NAVIANCEBLUE : PG}>
        <Create isNamePanel type={isNavianceUser ? 'navianceUser' : 'unknown'} />
      </Card.Highlight>
      <Card.Content>
        <Form onSubmit={handleOnSubmit(onSubmit)}>
          <Heading level="2" align="center">
            {isNavianceUser
              ? t`Enter a username to get started`
              : t`Please input your username to get started`}
          </Heading>
          <Field
            isRequired
            width="100%"
            margin="15px 0 0 0"
            label={t`Username`}
            desc={error.name}
            status={error.name ? 'error' : 'default'}>
            <TextInput size="small" {...register('name', { required: true })} />
          </Field>
          {isNavianceUser && (
            <ButtonLink
              variant="link"
              size="large"
              display="monochrome"
              color={PG}
              onClick={() => renderPanel('email')}>
              {t`I want to use my email address`}
            </ButtonLink>
          )}
          {isBelow13 && (
            <ButtonLink
              variant="link"
              size="large"
              display="monochrome"
              onClick={() => renderPanel('email')}>
              {t`I want to use my email address`}
            </ButtonLink>
          )}
          <Controls useControls>
            {(!isBelow13 && !isNavianceUser) && <Back onClick={() => renderPanel('mainType')} />}
            <Submit
              text={t`Next`}
              isLoading={isValidating}
              isDisabled={isInvalid || isValidating}
            />
          </Controls>
        </Form>
      </Card.Content>
      <Card.Detour>
        <Text>
          {t`Already have an account?`} <Link href="/signin">{t`Sign in here`}</Link>
        </Text>
      </Card.Detour>
    </Card>
  )
}

Username.propTypes = { ...props }

export { Username }
